import React, { useState, useEffect } from 'react';
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import adminStatusContext from '../context/adminStatusContext';
import UserLoginPage from '../components/UserLoginPage';
import SplashScreen from '../components/SplashScreen';

import QuizApi from '../QuizApi';
import { syncQuizBgColor } from '../utils/quizBgColor';
import { playLoginChime } from '../utils/loginChime';

const SPLASH_DURATION_MS = 2300;

function setStatusFromUserSessionData(status, sessionData) {

  return { ...status, loading: false, error: false,

    isUserLoggedIn: true,
    showLoginForm: false,

    userId: sessionData.userId,
    userName: sessionData.userName,
    userFullName: sessionData.userFullName,
    userEmail: sessionData.userEmail,

    orgId: sessionData.orgId,
    orgName: sessionData.orgName,

    orgHasLogo: sessionData.orgHasLogo,
    orgQuizWaitingRoomMediaId: sessionData.quizWaitingRoomMediaId,
    quizBackgroundCss: sessionData.quizBackgroundCss,

    isSuperUser: !!sessionData.isSuperUser,
    isFreeTier: !!sessionData.isFreeTier,
    isOrgAdmin: !!sessionData.isOrgAdmin
  };
}

export default function QuizAdminStatusProvider(props) {

  const initialState = {
    initial: true,
    isUserLoggedIn: false,
    showLoginForm: false,
    loading: false
  };

  const [status, setStatus] = useState(initialState);
  const [showSplash, setShowSplash] = useState(false);

  // Apply org-configured background color (if any) and persist for next load
  useEffect(() => {
    if (status.isUserLoggedIn) {
      syncQuizBgColor(status.quizBackgroundCss || null);
    }
  }, [status.isUserLoggedIn, status.quizBackgroundCss]);

  // ===============================================================
  // Callback to update the user session data from a login response.
  // Only called from an actual interactive login (UserLoginPage's submit),
  // never from the silent session-restore effect below — otherwise the
  // splash would replay on every admin page navigation, since this
  // provider remounts fresh per route (see App.js).
  // ===============================================================
  const onAuthSuccess = loginResponse => {
    setStatus(curStatus => setStatusFromUserSessionData(curStatus, loginResponse));
    setShowSplash(true);
    playLoginChime();
    setTimeout(() => setShowSplash(false), SPLASH_DURATION_MS);
  };

  // =======================================
  // Initial effect to fetch user session
  // =======================================
  useEffect(() => { 
    const start = async () => {

      try {

        if (!status.hasFetchedSession && status.initial)
        {
          setStatus(curStatus => ({ ...curStatus, loading: true, initial: false }));

          const sessionGetResult = await QuizApi.userSessionGet();
          if (!sessionGetResult) {
            throw new Error(sessionGetResult.errorMsg || "Invalid API response");
          }

          if (!sessionGetResult.success) 
          {
            if (sessionGetResult.response && sessionGetResult.response.authRequired) 
            {
              setStatus(curStatus => ({ ...curStatus, loading: false, showLoginForm: true }));
            }
            else
            {
              setStatus(curStatus => ({ ...curStatus, loading: false, error: true, errorMessage: sessionGetResult.error }));
            }
          }
          else
          {
            setStatus(curStatus => setStatusFromUserSessionData(curStatus, sessionGetResult.response));
          }
        }

      } catch (error) {
        console.error(error);
        setStatus(curStatus => ({
          ...curStatus,
          error: true,
          errorMessage: error.message
        }));
      }
    };

    start();

  }, [status]);

  return (
    <adminStatusContext.Provider value={status}>

      {status.loading && (
        <div id="adminLoaderScreen" className="quizAdminBg h-100 pt-4 pb-4 overflow-auto">
          <Container>
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" />
            </div>
          </Container>
        </div>
      )}

      {status.error && (
        <Alert variant="danger">
          <h4>Error: {status.errorMessage}</h4>
        </Alert>
      )}

      {status.showLoginForm && 
      <div className="quizAdminBg h-100 pt-4 pb-4 overflow-auto">
        <Container>
          <Row>
            <Col className="d-flex justify-content-center mt-4">
              <UserLoginPage onAuthSuccess={onAuthSuccess}/>
            </Col>
          </Row>
        </Container>
      </div>
      }

      {status.isUserLoggedIn && showSplash && <SplashScreen />}

      {status.isUserLoggedIn && !showSplash &&
      <div className="quizAdminBg h-100 pt-4 pb-4 overflow-auto">
        <Container>
          <Row>
            <Col className="d-flex justify-content-center mt-4">
              {props.children}
            </Col>
          </Row>
        </Container>
      </div>
      }

    </adminStatusContext.Provider>
  );
}

